/*
 * Project: Dynamedics Portal Web
 * Created Date: Friday, August 15th 2021, 4:30:28 am
 * Author: Senath Weerasinghe
 * -----
 * Last Modified: Friday December  17th 2021 12:41:51 pm
 * Modified By: Senath Weerasinghe at <sweerasinghe@mitrai.com>
 * -----
 * Copyright (c) 2021 Mitra Sparks
 * -----
 * HISTORY:
 * 2021-09-06	Screens loading issue handled.
 * 2021-12-17	Screens ordering fix done.
 */

import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { arrayMoveImmutable } from '../../utils/array-Move';

import {
  getBase64FromUrl,
  convertToBinaryStream
} from '../../utils/convertBase64';

// ----------------------------------------------------------------------

const initialState = {
  isLoadingScreens: false,
  isLoadingPillars: false,
  error: false,
  screensSuccess: false,
  pillarsSuccess: false,
  successOrg: false,
  pillarSubmit: false,
  screens: [],
  pillars: [],
  organizationalPillars: [],
  welcomeVideo: {},
  videoSuccess: false,
  isWelcomeVideoLoading: false
};

const slice = createSlice({
  name: 'appSettings',
  initialState,
  reducers: {
    // START LOADING
    startScreenLoading(state) {
      state.isLoadingScreens = true;
    },
    // START PILLAR LOADING
    startPillarLoading(state) {
      state.isLoadingPillars = true;
    },
    // START WELCOME VIDEO LOADING
    startWelcomeVideoLoading(state) {
      state.isWelcomeVideoLoading = true;
    },

    //START PILLAR SUBMIT
    startPillarSubmit(state) {
      state.pillarSubmit = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoadingScreens = false;
      state.isLoadingPillars = false;
      state.error = action.payload;
    },

    // GET ONBOARDING SCREENS
    getOnBoardScreensSuccess(state, action) {
      state.screens = action.payload;
      state.screensSuccess = false;
      state.isLoadingScreens = false;
    },

    // GET PILLARS
    getPillarsSuccess(state, action) {
      state.pillars = action.payload;
    },

    // GET ORG PILLARS
    getOrgPillarsSuccess(state, action) {
      state.isLoadingPillars = false;
      state.organizationalPillars = action.payload;
    },

    //CREATE ONBOARD SCREENS
    createOnboardSuccess(state) {
      // state.isLoadingScreens = false;
      state.screensSuccess = true;
    },

    //CREATE PILLARS
    createPillarsSuccess(state) {
      state.pillarsSuccess = true;
    },

    //CREATE NEW PILLARS
    createNewPillarsSuccess(state) {
      state.pillarSubmit = false;
    },

    //UPDATE ONBOARD LIST
    updateOnboardListSuccess(state) {
      // state.isLoadingScreens = false;
      state.screensSuccess = true;
    },

    //UPDATE THE PILLARS
    checkPillars(state, actions) {
      state.pillars = actions.payload;
    },

    //UPDATE ONBOARD SCREENS ORDER
    updateOrderOfScreens(state, actions) {
      //swapping the order
      let removedIndex = actions.payload.removedIndex;
      let addedIndex = actions.payload.addedIndex;

      //swapping the 2 screens
      //array move changed with arrayMoveImmutable
      state.screens = arrayMoveImmutable(
        [...state.screens],
        removedIndex,
        addedIndex
      );

      let order = 1;
      for (let i = 0; i <= 2; i++) {
        if (state.screens[i]) {
          state.screens[i].orderId = order;
          order++;
        }
      }
    },

    //DELETE ONBOARD SCREENS
    deleteScreen(state, actions) {
      state.screens = actions.payload;
    },
    // GET Welcome Video
    getWelcomeVideoSuccess(state, action) {
      state.welcomeVideo = action.payload;
      state.videoSuccess = false;
      state.isWelcomeVideoLoading = false;
    },
    deleteWelcomeVideoSuccess(state, action) {
      // const { videoId } = action.payload;
      state.isWelcomeVideoLoading = false;
      state.welcomeVideo = action.payload;
    },
    createWelcomeSuccess(state, action) {
      state.isWelcomeVideoLoading = false;
      state.videoSuccess = true;
    }
  }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------
//exporting actions
export const {
  checkPillars,
  updateOrderOfScreens,
  deleteScreen
} = slice.actions;

export function getOnboardScreens() {
  return async (dispatch) => {
    try {
      const response = await axios.get('/settings/onboarding');
      dispatch(slice.actions.getOnBoardScreensSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// ----------------------------------------------------------------------

export function getPillars() {
  return async (dispatch) => {
    try {
      const response = await axios.get('/settings/meta?type=pillars');
      dispatch(slice.actions.getPillarsSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// ----------------------------------------------------------------------

export function getOrgPillars() {
  return async (dispatch) => {
    try {
      const response = await axios.get('/settings/pillars');
      dispatch(slice.actions.getOrgPillarsSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// ----------------------------------------------------------------------

export function createPillar(data) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startPillarLoading());
      const response = await axios.post('/settings/pillars', {
        newOrganizationalPillars: [...data]
      });
      dispatch(slice.actions.createPillarsSuccess());
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// ----------------------------------------------------------------------

export function createNewPillar(data) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startPillarSubmit());
      const response = await axios.post('/settings/pillars', {
        newOrganizationalPillars: [...data]
      });
      dispatch(slice.actions.createNewPillarsSuccess());
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// ----------------------------------------------------------------------

export function createScreen(data) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startScreenLoading());
      const response = await axios.post('/settings/onboarding', {
        screens: [...data]
      });
      dispatch(slice.actions.createOnboardSuccess());
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function updateOnboardScreens(data) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startScreenLoading());

      const response = await axios.post('/settings/onboarding', {
        screens: [...data]
      });
      dispatch(slice.actions.updateOnboardListSuccess());
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------
export async function createWelcomeVideo(data) {
  try {
    let dataSet = { ...data };

    dataSet.coverImage = await getBase64FromUrl(dataSet.coverImage.preview);
    dataSet.videoType = dataSet.welcomeVideo.type;

    const response = await axios.post('/settings/welcome-video', {
      ...dataSet
    });

    let blobData = await convertToBinaryStream(
      dataSet.welcomeVideo.file,
      dataSet.videoType
    );

    await fetch(response.data.data.signedUrl, {
      method: 'PUT',
      body: blobData
    });

    const confirmVideoUpload = await axios.post(
      `/settings/welcome-video/${response.data.data.id}/video`
    );

    return confirmVideoUpload;
  } catch (error) {
    return { statusCode: -1 };
  }
}

export function getWelcomeVideo() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startWelcomeVideoLoading());
      const response = await axios.get('/settings/welcome-video');
      dispatch(slice.actions.getWelcomeVideoSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createWelcomeSuccess(data) {
  return async (dispatch) => {
    dispatch(slice.actions.createWelcomeSuccess(data));
  };
}

export function deleteWelcomeVideo() {
  return async (dispatch) => {
    try {
      const response = await axios.delete('/settings/welcome-video');
      dispatch(slice.actions.deleteWelcomeVideoSuccess(response.data.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
